<template>
  <div class="attachment-container">
    <!-- TODO: add animation if dragging the file here -->
    <div v-if="isDragging" class="drag-overlay"></div>
    <div
      class="attachment-field d-flex flex-column align-items-center justify-content-center gap-3"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
    >
      <div
        v-if="fileName"
        class="attachment-field__file-attached d-flex flex-column align-items-center justify-content-center gap-2"
      >
        <img src="@/assets/images/xls-icon.png" width="43" />
        <p>{{ fileName }}</p>

        <b-spinner v-if="isLoading" />
      </div>

      <div v-else class="attachment-field__empty-state">
        <p>{{ $t('global.importData.chooseFile') }}</p>
        <p>{{ $t('global.importData.maksFile2MB') }}</p>
      </div>

      <McButton
        :text="$t('global.importData.selectFile')"
        variant="secondary"
        @click="handleLoadFile"
        :disabled="isLoading"
      />

      <input
        type="file"
        ref="file-input-ref"
        :accept="fileAccepted.join(', ')"
        @change="handleFileChanged"
        hidden
      />
    </div>
  </div>
</template>

<script>
import McButton from '@/components/McButton'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'

import * as Import from '@/services/rest-api/lib/delivery/import'
import { setDeliveryImportIdtoLs, getDeliveryImportIdFromLs } from '../lib/helper'
import { errorMappings } from '../lib/error-mappings'

export default {
  components: {
    McButton,
  },
  mixins: [Helper],
  props: {
    selectedUserGroup: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:isDisableNextBtn', 'selectFile'],
  data() {
    return {
      isLoading: false,
      isDragging: false,

      fileName: '',
      fileSize: null,
      fileId: null,
      file: undefined,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    hasUserGroupFeature() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])
      const isSuperUser = this.isSuperUser()
      
      return hasScope && isSuperUser
    },
    fileAccepted() {
      return [
        '.csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/wps-office.xlsx',
      ]
    },
  },
  mounted() {
    const existingImportId = getDeliveryImportIdFromLs()

    if (!existingImportId) return

    this.redirectToProcessFile(existingImportId)
  },
  methods: {
    async HandleProcessFile() {
      if (this.fileId == null) return

      this.$emit('update:isDisableNextBtn', true)

      const payload = { 
        file_id: this.fileId,
      }
      
      // Add user_group_id if user group is selected and feature is enabled
      if (this.hasUserGroupFeature && this.selectedUserGroup?.id) {
        payload.user_group_id = this.selectedUserGroup.id
      }

      const res = await Import.createDelivery(payload)
      if (res.errors) {
        this.$emit('update:isDisableNextBtn', true)
        return
      }

      setDeliveryImportIdtoLs(res.data.id)

      this.redirectToProcessFile(res.data.id)
    },
    redirectToProcessFile(importId) {
      this.$router.push({
        name: 'Delivery Import Detail',
        params: { id: importId },
      })
    },
    handleLoadFile() {
      if (this.isLoading) {
        this.$mcToast.warning(this.$t('global.alert.text.pleaseWait'))
        return
      }

      if (this.hasUserGroupFeature) {
        this.$emit('selectFile')
      } else {
        this.openFileDialog()
      }
    },
    openFileDialog() {
      this.$refs['file-input-ref'].click()
    },
    handleDragOver(event) {
      event.preventDefault()
      this.isDragging = true
    },
    handleDragLeave() {
      this.isDragging = false
    },
    handleDrop(event) {
      event.preventDefault()
      this.isDragging = false

      if (this.hasUserGroupFeature && !this.selectedUserGroup?.id) {
        this.$mcToast.warning(this.$t('delivery.upload.selectUserGroupFirst'))
        this.$emit('selectFile')
        return
      }

      this.isLoading = true
      this.file = event.dataTransfer.files[0]

      this.uploadFile()
    },
    resetInputFile(event) {
      this.fileName = ''
      this.fileSize = null
      this.file = undefined

      if (event) {
        event.target.value = ''
      }

      this.isLoading = false
    },
    handleFileChanged(event) {
      this.isLoading = true

      const [file] = event.target.files
      if (!file) {
        this.isLoading = false
        return
      }

      this.file = file
      this.uploadFile(event)
    },
    async uploadFile(event = undefined) {
      if (!this.fileAccepted.includes(this.file.type)) {
        this.$mcToast.error(this.$t('delivery.upload.invalidFileFormat'))

        this.resetInputFile(event)
        return
      }

      let fileSize = this.file.size
      const MAX_FILE_SIZE = 1024 * 1024 * 2 // 2MB
      if (fileSize > MAX_FILE_SIZE) {
        this.$mcToast.error(this.$t('delivery.upload.fileSizeExceeded'))

        this.resetInputFile(event)
        return
      }

      this.fileName = this.file.name

      fileSize = this.file.size / 1024 / 1024
      this.fileSize = fileSize.toFixed(2)

      let res = await Import.uploadFile({ type: this.file.type, file: this.file })

      if (res.errors) {
        let errorMessage = this.$t('delivery.upload.uploadFailed')

        const errorCode = res.errors_v2?.issues?.[0]?.code

        let responseMessage = ''
        if (errorCode && errorMappings[errorCode]) {
          responseMessage = errorMappings[errorCode]
        } else if (res.errors_v2?.issues?.[0]?.message) {
          responseMessage = res.errors_v2.issues[0].message
        } else if (res.errors && typeof res.errors === 'object') {
          const firstKey = Object.keys(res.errors)[0]
          if (firstKey) {
            const errorVal = res.errors[firstKey]
            responseMessage = Array.isArray(errorVal) ? errorVal[0] : errorVal
          }
        } else if (typeof res.errors === 'string') {
          responseMessage = res.errors
        }

        if (res.status == 413) {
          errorMessage = this.$t('delivery.upload.fileSizeExceeded')
        } else if (responseMessage) {
          const uploadFailedPrefix = this.$t('delivery.upload.uploadFailedPrefix')
          errorMessage = responseMessage.startsWith(uploadFailedPrefix)
            ? responseMessage
            : `${uploadFailedPrefix} ${responseMessage}`
        } else if (res.status == 400) {
          errorMessage = this.$t('delivery.upload.invalidTemplateFile')
        }

        this.$mcToast.error(errorMessage)

        this.resetInputFile(event)
        return
      }

      this.$emit('update:isDisableNextBtn', false)

      this.fileId = res.data.id
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.attachment-container {
  position: relative;
  width: 100%;
  height: 100%;

  .attachment-field {
    border: 2px dashed $mep-primary-100;
    border-radius: 4px;
    width: 100%;
    height: 100%;

    p {
      line-height: 20px;
      margin: 0;
      text-align: center;
    }

    &__empty-state {
      color: $mep-color-text-300;
    }

    &__file-attached {
      width: 75%;
      text-align: center;
      font-weight: 600;
    }

    &__progress-bar {
      width: 100%;
    }
  }
}
</style>
